import * as React from 'react';
import Box from '@mui/material/Box';
import { columns, generateRows } from './partials';
import { Breakdown, CommonCard, Table } from '@organisms';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import {
  callDeleteBillingAccount,
  useBillingAccountsDetailedList,
  useBillingAccountsSummary,
  callTogglePin,
  callGetMarkupValues,
  usePriceModelHistory
} from '@api';
import { useState, useContext, useMemo, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TableContext, useDebouncedValue } from '@particles';
import { Alert, Button, TextField, TextView } from '@atoms';
import { RouteComponentProps, navigate } from '@reach/router';
import AddBillingAccount from './add-account';
import {
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableHead,
  TableRow as MuiTableRow,
  Typography
} from '@mui/material';

const FILTER_DEFAULTS = { search: '', type: 'b', status: 'live', priceModel: 'b' };

const parseFilters = (search?: string) => {
  const params = new URLSearchParams(search ?? '');
  return {
    search: params.get('search') ?? FILTER_DEFAULTS.search,
    type: params.get('type') ?? FILTER_DEFAULTS.type,
    status: params.get('status') ?? FILTER_DEFAULTS.status,
    priceModel: params.get('priceModel') ?? FILTER_DEFAULTS.priceModel
  };
};

const BillingAccount: React.FC<RouteComponentProps> = (props) => {
  const {
    state: {
      billingAccountPagination: { page, perPage, orderBy }
    }
  } = useContext(TableContext);
  const [loading, setLoading] = useState<boolean>(false);
  const [openAddToken, setOpenAddToken] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [deleteTarget, setDeleteTarget] = useState<BillingAccount | undefined>();
  const [forceDelete, setForceDelete] = useState(false);
  const [historyTarget, setHistoryTarget] = useState<BillingAccount | undefined>();
  const [selectedBillingAccount, setSelectedBillingAccount] = useState<BillingAccount>();
  const initialFilters = useRef(parseFilters(props.location?.search)).current;
  const [search, setSearch] = useState<string>(initialFilters.search);
  const debouncedSearch = useDebouncedValue(search, 350);
  const [selectType, setSelectType] = useState<string>(initialFilters.type);
  const [selectstatus, setSelectStatus] = useState<string>(initialFilters.status);
  const [selectPriceModel, setSelectPriceModel] = useState<string>(initialFilters.priceModel);

  const hasActiveFilters =
    !!search || selectType !== 'b' || selectstatus !== 'b' || selectPriceModel !== 'b';

  const clearFilters = () => {
    setSearch('');
    setSelectType('b');
    setSelectStatus('b');
    setSelectPriceModel('b');
  };

  // Mirror the current filter state into the URL query string so views are
  // shareable / bookmarkable. Only non-default values are written; back/forward
  // reconciles state from the URL below.
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selectType !== FILTER_DEFAULTS.type) params.set('type', selectType);
    if (selectstatus !== FILTER_DEFAULTS.status) params.set('status', selectstatus);
    if (selectPriceModel !== FILTER_DEFAULTS.priceModel) params.set('priceModel', selectPriceModel);
    const nextQuery = params.toString();
    const currentQuery = (props.location?.search ?? '').replace(/^\?/, '');
    if (nextQuery === currentQuery) return;
    const pathname = props.location?.pathname ?? window.location.pathname;
    navigate(nextQuery ? `${pathname}?${nextQuery}` : pathname, { replace: true });
  }, [debouncedSearch, selectType, selectstatus, selectPriceModel, props.location?.pathname, props.location?.search]);

  // Reconcile state back from the URL on back/forward or shared links.
  useEffect(() => {
    const parsed = parseFilters(props.location?.search);
    if (parsed.search !== search) setSearch(parsed.search);
    if (parsed.type !== selectType) setSelectType(parsed.type);
    if (parsed.status !== selectstatus) setSelectStatus(parsed.status);
    if (parsed.priceModel !== selectPriceModel) setSelectPriceModel(parsed.priceModel);
  }, [props.location?.search]);

  // AI Model filter options — drawn from the markup-margin catalogue. Same
  // 30-min cached query key the add-account modal and accountDetails use,
  // so this typically hits the cache.
  const markupQuery = useQuery({
    queryKey: ['markup-values/catalogue'],
    queryFn: () => callGetMarkupValues(),
    staleTime: 30 * 60 * 1000
  });
  const priceModelOptions = useMemo(() => {
    if (markupQuery.data?.code !== 200 || !markupQuery.data.data?.data) return [];
    // priceModel column stores the full MarkupMargins.type slug (e.g.
    // `ai_processing_sr_h1`) — same as what add-account writes. This list is a
    // table filter, not an assignment, so it deliberately includes the AI fallback
    // tier: no account should ever be on it, and leaving it selectable is how you
    // find strays if a script or direct SQL ever put one there.
    return markupQuery.data.data.data
      .filter((item) => item.type.startsWith('ai_processing'))
      .map((item) => ({
        value: item.type,
        label: item.title
      }));
  }, [markupQuery.data]);

  const { data: summaryResponse, isFetching: summaryFetching } = useBillingAccountsSummary();
  const summary = summaryResponse?.data;


  const {
    data: response,
    isFetching,
    refetch
  } = useBillingAccountsDetailedList({
    type: selectType == 'b' ? undefined : selectType,
    status: selectstatus == 'b' ? undefined : selectstatus,
    companyName: debouncedSearch,
    pageNumber: page,
    pageSize: perPage,
    sortBy: orderBy,
    priceModel: selectPriceModel == 'b' ? undefined : selectPriceModel
  });

  const onAction = async (token: BillingAccount, _value: unknown, type: string) => {
    if (type == 'edit') {
      setOpenAddToken(true);
      setSelectedBillingAccount(token);
    } else if (type == 'delete') {
      setDeleteTarget(token);
      setForceDelete(false);
    } else if (type == 'pin') {
      await callTogglePin(token.id);
      refetch();
    } else if (type == 'history') {
      setHistoryTarget(token);
    }
  };

  const historyQuery = usePriceModelHistory({
    billingAccountId: historyTarget?.id,
    enabled: Boolean(historyTarget)
  });

  const deleteApi = async () => {
    if (!deleteTarget) {
      return;
    }

    setLoading(true);
    const { code, error } = await callDeleteBillingAccount(deleteTarget.id, {
      forceDelete
    });
    if (code >= 400 && error) {
      setError(error.message);
      setTimeout(() => {
        setError(undefined);
        setLoading(false);
      }, 3000);
    } else {
      setLoading(false);
      setDeleteTarget(undefined);
      refetch();
    }
  };

  const handleChange = (event: SelectChangeEvent) => {
    setSelectType(event.target.value);
  };
  const handleChangestatus = (event: SelectChangeEvent) => {
    setSelectStatus(event.target.value);
  };
  const handleChangePriceModel = (event: SelectChangeEvent) => {
    setSelectPriceModel(event.target.value);
  };

  const rowData: TableRow[] =
    response?.data && response?.data?.data ? generateRows(response?.data.data, onAction) : [];

  return (
    <Box m={4}>
      {error && (
        <Box sx={{ mb: 1 }}>
          <Alert message={error} severity={'error'} />
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)'
          },
          gap: 2,
          gridAutoRows: '1fr',
          mb: 3
        }}
      >
        <CommonCard
          title="Total Accounts"
          count={summary?.total ?? 0}
          caption="By status"
          icon={<BusinessOutlinedIcon />}
          accent="neutral"
          loading={summaryFetching && !summary}
        >
          <Breakdown
            items={[
              { label: 'Live', value: summary?.live ?? 0 },
              { label: 'Staging', value: summary?.staging ?? 0 },
              { label: 'Inactive', value: summary?.inactive ?? 0 }
            ]}
          />
        </CommonCard>
        <CommonCard
          title="Product Mix"
          caption="By product"
          icon={<CategoryOutlinedIcon />}
          accent="highlight"
          loading={summaryFetching && !summary}
        >
          <Breakdown
            items={[
              { label: 'Core', value: summary?.core ?? 0 },
              { label: 'Negotiator', value: summary?.negotiator ?? 0 },
              { label: 'Both', value: summary?.multiProduct ?? 0 }
            ]}
          />
        </CommonCard>
      </Box>
      <Stack direction={'row'} sx={{ mb: 2, alignItems: 'end' }}>
        <TextField
          label={'Search Account Name'}
          name={'Search Account Name'}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <Stack mt={1.5} ml={2}>
          <TextView $fontWeight={400} $fontSize={14}>
            Account Type
          </TextView>
          <Select
            value={selectType}
            onChange={handleChange}
            displayEmpty
            size={'small'}
            sx={{ backgroundColor: '#ffffff', fontSize: 14, mt: 1 }}
            inputProps={{ 'aria-label': 'Without label' }}>
            <MenuItem key={0} value={'b'}>
              All
            </MenuItem>
            <MenuItem key={0} value={'c'}>
              Core
            </MenuItem>
            <MenuItem key={0} value={'r'}>
              Negotiator
            </MenuItem>
          </Select>
        </Stack>
        <Stack mt={1.5} ml={2}>
          <TextView $fontWeight={400} $fontSize={14}>
            Status Type
          </TextView>
          <Select
            value={selectstatus}
            onChange={handleChangestatus}
            displayEmpty
            size="small"
            sx={{ backgroundColor: '#ffffff', fontSize: 14, mt: 1 }}
            inputProps={{ 'aria-label': 'Without label' }}>
            <MenuItem key="all" value="b">
              All
            </MenuItem>
            <MenuItem key="live" value="live">
              Live
            </MenuItem>
            <MenuItem key="staging" value="staging">
              Staging
            </MenuItem>
            <MenuItem key="inactive" value="inactive">
              Inactive
            </MenuItem>
          </Select>
        </Stack>
        <Stack mt={1.5} ml={2}>
          <TextView $fontWeight={400} $fontSize={14}>
            AI Model
          </TextView>
          <Select
            value={selectPriceModel}
            onChange={handleChangePriceModel}
            displayEmpty
            size="small"
            sx={{ backgroundColor: '#ffffff', fontSize: 14, mt: 1, minWidth: 160 }}
            inputProps={{ 'aria-label': 'AI Model' }}>
            <MenuItem key="all" value="b">
              All
            </MenuItem>
            {priceModelOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Button
          onClick={clearFilters}
          disabled={!hasActiveFilters}
          sx={{ mr: 1 }}
        >
          {'Clear Filters'}
        </Button>
        <Button onClick={() => setOpenAddToken(true)}>{'Add Account'}</Button>
      </Stack>

      <Table
        name="billing-accounts"
        columns={columns}
        data={rowData}
        isLoading={isFetching || loading}
        pagination={response?.data}
        persistColumnPrefs
      />

      <AddBillingAccount
        open={openAddToken}
        selectedBillingAccount={selectedBillingAccount}
        onClose={() => {
          setOpenAddToken(false);
          setSelectedBillingAccount(undefined);
        }}
        onSuccess={() => {
          setOpenAddToken(false);
          setSelectedBillingAccount(undefined);
          refetch();
        }}
      />

      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(undefined)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Account</DialogTitle>
        <DialogContent>
          {deleteTarget && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body1">
                <strong>{deleteTarget.companyName}</strong>
                {' '}({deleteTarget.locationId ? 'Core' : 'Negotiator'})
              </Typography>

              {(deleteTarget.srtValuesBasedSubscription ?? 0) > 0 && (
                <Alert
                  message={`This account has ${deleteTarget.srtValuesBasedSubscription} consumed tokens this billing cycle.`}
                  severity="warning"
                />
              )}

              <Divider />

              <Typography variant="body2" color="text.secondary">
                The following data will be marked for deletion and permanently removed after 30 days:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>All usage records (SRT, WhatsApp, Twilio, AI, Transactions)</li>
                <li>All prompt templates</li>
                {deleteTarget.locationId && (
                  <li>All location data (contacts, metadata, offices, users, artifacts, broadcasts, lead events, enquiries, integrations)</li>
                )}
              </Typography>

              {(deleteTarget.srtValuesBasedSubscription ?? 0) > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={forceDelete}
                      onChange={(e) => setForceDelete(e.target.checked)}
                      color="warning"
                    />
                  }
                  label="I understand, force delete anyway"
                />
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(undefined)}>Cancel</Button>
          <Button
            onClick={() => {
              deleteApi();
              setDeleteTarget(undefined);
            }}
            disabled={(deleteTarget?.srtValuesBasedSubscription ?? 0) > 0 && !forceDelete}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!historyTarget} onClose={() => setHistoryTarget(undefined)} maxWidth="md" fullWidth>
        <DialogTitle>
          AI Model History
          {historyTarget?.companyName && (
            <Typography variant="body2" color="text.secondary">
              {historyTarget.companyName}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {historyQuery.isLoading && (
            <Stack direction="row" justifyContent="center" py={4}>
              <CircularProgress size={24} />
            </Stack>
          )}
          {historyQuery.error && (
            <Alert message="Could not load history." severity="error" />
          )}
          {!historyQuery.isLoading && !historyQuery.error && (
            <MuiTable size="small">
              <TableHead>
                <MuiTableRow>
                  <TableCell>When</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>By</TableCell>
                </MuiTableRow>
              </TableHead>
              <TableBody>
                {(historyQuery.data ?? []).map((row) => (
                  <MuiTableRow key={row.id}>
                    <TableCell>{new Date(row.changedAt).toLocaleString()}</TableCell>
                    <TableCell>{row.oldPriceModel ?? '— (initial)'}</TableCell>
                    <TableCell>{row.newPriceModel}</TableCell>
                    <TableCell>{row.changedByUserName ?? '—'}</TableCell>
                  </MuiTableRow>
                ))}
                {(historyQuery.data ?? []).length === 0 && (
                  <MuiTableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" align="center" py={2}>
                        No changes recorded yet.
                      </Typography>
                    </TableCell>
                  </MuiTableRow>
                )}
              </TableBody>
            </MuiTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(undefined)}>Close</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BillingAccount;
